import React from 'react';
import { Form, Formik } from 'formik';
import { Grid, ISpruceFieldArrayProps, SpruceArrayField, Upload } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { UploadDocument } from './UploadDocument';
import { uploadDocumentValidation } from './uploadDocumentValidation';
import { IUploadDocument } from 'types/order/documents/upload/interfaces';
import { AutoSubmit } from 'components/shared/AutoSubmit';
import { IDocumentCategory } from '../../../../types/order/documents/IDocumentCategoryType';
import { requiresGoodThroughDate } from '../../../../types/order/documents/IDocumentCategoryType';

interface IUploadsDocumentControllerProps {
  documentCategoriesByName: Record<string, IDocumentCategory>;
  documentCategoryFilter?: (category: IDocumentCategory) => boolean;
  dropdownMessage?: string;
  onSubmit: (documents: IUploadDocument[]) => Promise<any>;
  onReset: () => void;
  uploadFetch: IFetchStatus;
}

export const UploadDocumentController: React.FC<IUploadsDocumentControllerProps> = ({
  documentCategoriesByName,
  documentCategoryFilter,
  dropdownMessage,
  onSubmit,
  onReset,
  uploadFetch,
}) => {
  const onFileUpload = (onChange: (value: any) => void, files: File[]) => {
    const uploadDocuments = files.map((file) => ({ file }));
    onChange(uploadDocuments);
  };
  const isAutoSubmittable = (files: IUploadDocument[]): boolean => {
    let formCanBeAutoSubmit = true;
    files.forEach((file) => {
      if (requiresGoodThroughDate(file.category, documentCategoriesByName)) formCanBeAutoSubmit = false;
    });
    return formCanBeAutoSubmit;
  };

  return (
    <Formik
      initialValues={{
        files: [],
      }}
      validationSchema={uploadDocumentValidation(documentCategoriesByName)}
      isInitialValid={false}
      enableReinitialize={true}
      onSubmit={async (values, { resetForm }) => {
        try {
          await onSubmit(values.files);
          if (isAutoSubmittable(values.files)) {
            setTimeout(() => {
              resetForm();
              onReset();
            }, 1500);
          }
        } catch (e: any) {}
      }}
      onReset={(values, { resetForm }) => {
        onReset();
      }}
    >
      {({ errors, values, isValid, setSubmitting, handleSubmit, handleReset }) => {
        return (
          <Form onSubmit={handleSubmit} onReset={handleReset}>
            <Grid>
              <SpruceArrayField name={'files'}>
                {({ field, formik }: ISpruceFieldArrayProps) => (
                  <>
                    {field.value && field.value.length > 0 ? (
                      <>
                        {field.value.map((document: IUploadDocument, index: number) => (
                          <UploadDocument
                            documentCategoriesByName={documentCategoriesByName}
                            documentCategoryFilter={documentCategoryFilter}
                            key={document.file.name}
                            isLoading={uploadFetch.fetching}
                            isComplete={uploadFetch.fetched}
                            hasError={uploadFetch.fetched && !uploadFetch.success}
                            formIsValid={isValid}
                            index={index}
                            onDeleteDocument={() => {
                              field.remove(index);
                              formik.setFieldTouched(field.name);
                            }}
                            onResetForm={onReset}
                            onSubmit={handleSubmit}
                          />
                        ))}
                      </>
                    ) : (
                      <Upload
                        onFileUpload={(files) => {
                          onFileUpload(field.onChange, files);
                        }}
                        multiple={false}
                        message={dropdownMessage}
                      />
                    )}
                  </>
                )}
              </SpruceArrayField>
            </Grid>
            {isAutoSubmittable(values.files) ? (
              <AutoSubmit values={values} submitForm={handleSubmit} isValid={isValid} />
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
};
